import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import { codesFromConfirmLink, confirmChangeBody, hasEveryCode } from '../../lib/profile-change.js';
import {
    CONNECTED_ACCOUNTS_EMPTY,
    CONNECTED_ACCOUNTS_LIST,
    connectedAccountsView,
    IDENTITY_STATUS_ERROR,
    IDENTITY_STATUS_LOADED,
    IDENTITY_STATUS_LOADING,
    identityDisplayName,
    needsReconnect,
    providerIcon,
    providerLabel,
} from '../../lib/providers.js';
import { css, html } from '../../vendor/lit.min.js';

class ProfileView extends BaseComponent {
    static properties = {
        user: { type: Object, state: true },
        error: { type: String, state: true },
        success: { type: String, state: true },
        editing: { type: Boolean, state: true },
        editUsername: { type: String, state: true },
        editEmail: { type: String, state: true },
        editPictureFile: { type: Object, state: true },
        uploadingPicture: { type: Boolean, state: true },
        editPicturePreview: { type: String, state: true },
        editPictureURL: { type: String, state: true },
        usernameStatus: { type: String, state: true },
        // Confirmation
        awaitingCode: { type: Boolean, state: true },
        verificationEmail: { type: String, state: true },
        code: { type: String, state: true },
        // An email change also needs a code sent to the address being replaced.
        currentEmail: { type: String, state: true },
        currentEmailCode: { type: String, state: true },
        pendingCurrentEmail: { type: String, state: true },
        loading: { type: Boolean, state: true },
        showDeleteConfirm: { type: Boolean, state: true },
        pendingChanges: { type: Object, state: true },
        pendingVerificationEmail: { type: String, state: true },
        // Connected accounts
        identities: { type: Array, state: true },
        availableProviders: { type: Array, state: true },
        identityStatus: { type: String, state: true },
        identityError: { type: String, state: true },
        identityBusy: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .profile-card { max-width: 500px; margin: 0 auto; }
        .avatar { width: 96px; height: 96px; border-radius: 50%; object-fit: cover; border: 2px solid #dee2e6; }
        .avatar-placeholder { width: 96px; height: 96px; border-radius: 50%; background: #e9ecef; display: flex; align-items: center; justify-content: center; font-size: 2.5rem; color: #adb5bd; border: 2px solid #dee2e6; }
        .avatar-edit { cursor: pointer; position: relative; display: inline-block; }
        .avatar-edit:hover::after { content: ''; position: absolute; inset: 0; border-radius: 50%; background: rgba(0,0,0,0.3); }
        .avatar-edit:hover .avatar-edit-icon { opacity: 1; }
        .avatar-edit-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-size: 1.5rem; opacity: 0; z-index: 1; pointer-events: none; }
        .avatar-uploading { position: absolute; inset: 0; border-radius: 50%; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; }
        .avatar-input { display: none; }
        .code-input { font-size: 1.25rem; text-align: center; letter-spacing: 0.4rem; max-width: 200px; }
    `;

    constructor() {
        super();
        this.user = null;
        this.error = '';
        this.success = '';
        this.editing = false;
        this._resetEditState();
        this._usernameTimer = null;
        this.showDeleteConfirm = false;
        this.uploadingPicture = false;
        this.pendingChanges = null;
        this.pendingVerificationEmail = '';
        this.pendingCurrentEmail = '';
        // Connected accounts are not edit state — cancelling an edit must not
        // discard what was loaded, or the section disappears until a reload.
        this.identities = [];
        this.availableProviders = [];
        this.identityStatus = IDENTITY_STATUS_LOADING;
        this.identityError = '';
        this.identityBusy = false;
    }

    _resetEditState() {
        this.editUsername = '';
        this.editEmail = '';
        this.editPictureFile = null;
        this.editPicturePreview = null;
        this.editPictureURL = null;
        this.usernameStatus = '';
        this.awaitingCode = false;
        this.verificationEmail = '';
        this.code = '';
        this.currentEmail = '';
        this.currentEmailCode = '';
        this.loading = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadIdentities();
        this._loadProfile().then(() => {
            // On /profile/confirm?code=… (or ?current_code=… from the current
            // address's email), fill in the code. Confirm straight away only
            // when the link carried everything the change needs.
            const linked = codesFromConfirmLink(window.location.search);
            if ((linked.code || linked.currentEmailCode) && this.pendingChanges) {
                this._showPendingConfirm();
                this.code = linked.code;
                this.currentEmailCode = linked.currentEmailCode;
                if (hasEveryCode(this.code, this.currentEmailCode, this.currentEmail)) {
                    this._handleConfirm();
                }
            }
        });
    }

    updated() {
        this.shadowRoot?.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            if (/** @type {any} */ (window).bootstrap?.Tooltip) {
                new /** @type {any} */ (window).bootstrap.Tooltip(el);
            }
        });
    }

    async _loadProfile() {
        try {
            const data = await api.get('/api/v1/me');
            this.user = data.user;
            this.pendingChanges = data.pending_changes || null;
            this.pendingVerificationEmail = data.pending_verification_email || '';
            this.pendingCurrentEmail = data.pending_current_email || '';
        } catch {
            authState.clearTokens();
            authState.isAuthenticated = false;
            authState.user = null;
            authState.notify();
        }
    }

    _startEditing() {
        // Dispose tooltips before switching views
        this.shadowRoot?.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            const tooltip = /** @type {any} */ (window).bootstrap?.Tooltip?.getInstance(el);
            if (tooltip) tooltip.dispose();
        });
        this.editing = true;
        this.editUsername = this.user.username;
        this.editEmail = this.user.email;
        this.editPictureFile = null;
        this.editPicturePreview = null;
        this.editPictureURL = null;
        this.error = '';
        this.success = '';
        this.usernameStatus = '';
    }

    _cancelEditing() {
        this.editing = false;
        this._resetEditState();
        this.error = '';
    }

    _handleAvatarClick() {
        if (!this.editing) return;
        /** @type {HTMLElement | null} */
        const input = this.shadowRoot.querySelector('.avatar-input');
        input?.click();
    }

    _handleAvatarChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            this.error = 'Please select an image file';
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            this.error = 'Image must be under 2MB';
            return;
        }
        this.editPictureFile = file;
        this.editPicturePreview = URL.createObjectURL(file);
        this.error = '';
    }

    _handleUsernameInput(e) {
        this.editUsername = e.target.value;
        this.usernameStatus = '';
        if (this._usernameTimer) clearTimeout(this._usernameTimer);
        if (this.editUsername.length >= 3 && this.editUsername !== this.user.username) {
            this._usernameTimer = setTimeout(() => this._checkUsername(), 500);
        }
    }

    async _checkUsername() {
        try {
            const data = await api.post('/api/v1/check-username', { username: this.editUsername });
            this.usernameStatus = data.available ? 'available' : data.error || 'Not available';
        } catch {
            this.usernameStatus = '';
        }
    }

    _hasChanges() {
        return (
            this.editUsername !== this.user.username ||
            this.editEmail !== this.user.email ||
            this.editPictureFile !== null
        );
    }

    _canSave() {
        if (!this._hasChanges()) return false;
        if (this.editUsername !== this.user.username && this.usernameStatus !== 'available')
            return false;
        if (this.editEmail !== this.user.email && !this.editEmail) return false;
        return true;
    }

    async _handleSave() {
        this.error = '';
        this.loading = true;

        try {
            // Upload picture first if changed
            if (this.editPictureFile) {
                this.uploadingPicture = true;
                const formData = new FormData();
                formData.append('avatar', this.editPictureFile);
                const token = authState.getAccessToken();
                const uploadResp = await fetch(`${getBackendUrl()}/api/v1/avatar`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                });
                this.uploadingPicture = false;
                const uploadData = await uploadResp.json();
                if (!uploadResp.ok) throw new Error(uploadData.message || 'Upload failed');
                this.editPictureURL = uploadData.picture;
            }

            // Build changes object (only include what changed)
            const changes = {};
            if (this.editUsername !== this.user.username) changes.username = this.editUsername;
            if (this.editEmail !== this.user.email) changes.email = this.editEmail;
            if (this.editPictureURL) changes.picture = this.editPictureURL;

            // Request confirmation code
            const data = await api.post('/api/v1/profile/request-change', changes);
            this.verificationEmail = data.verification_email;
            this.currentEmail = data.requires_current_email_code ? data.current_email : '';
            this.awaitingCode = true;
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    async _handleConfirm() {
        this.error = '';
        this.loading = true;

        try {
            const data = await api.post(
                '/api/v1/profile/confirm-change',
                confirmChangeBody(this.code, this.currentEmailCode, this.currentEmail),
            );
            this.user = data.user;
            authState.user = data.user;
            authState.permissions = data.permissions || [];
            authState.notify();
            this.editing = false;
            this._resetEditState();
            this.pendingChanges = null;
            this.pendingVerificationEmail = '';
            this.pendingCurrentEmail = '';
            this.success = 'Profile updated successfully';
            // Navigate to clean profile URL so success message shows
            if (window.location.pathname !== '/profile') {
                window.history.replaceState({}, document.title, '/profile');
            }
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    _handleDeleteAccount() {
        this.shadowRoot?.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            const tooltip = /** @type {any} */ (window).bootstrap?.Tooltip?.getInstance(el);
            if (tooltip) tooltip.dispose();
        });
        this.showDeleteConfirm = true;
    }

    async _confirmDeleteAccount() {
        this.showDeleteConfirm = false;
        try {
            const token = authState.getAccessToken();
            await fetch(`${getBackendUrl()}/api/v1/me`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            await authState.logout();
        } catch (err) {
            this.error = err.message;
        }
    }

    render() {
        if (!this.user) {
            return html`<div class="profile-card mt-5"><div class="card shadow"><div class="card-body p-4 text-center"><div class="spinner-border text-primary" role="status"></div></div></div></div>`;
        }

        return html`
            <div class="profile-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        ${this.success ? html`<div class="alert alert-success d-flex align-items-center gap-2"><i class="bi bi-check-circle-fill"></i> ${this.success}</div>` : ''}
                        ${this.error ? html`<div class="alert alert-danger d-flex align-items-start gap-2"><i class="bi bi-exclamation-triangle-fill" style="font-size: 1.25rem; margin-top: 0.1rem;"></i> <span>${this.error}</span></div>` : ''}
                        ${
                            !this.editing && this.pendingChanges
                                ? html`
                            <div class="alert alert-secondary d-flex align-items-center justify-content-between py-2">
                                <small><i class="bi bi-clock-history me-1"></i> You have pending profile changes.</small>
                                <button class="btn btn-secondary btn-sm" @click="${this._showPendingConfirm}">Confirm</button>
                            </div>
                        `
                                : ''
                        }

                        ${this.editing ? this._renderEditMode() : this._renderViewMode()}
                    </div>
                </div>
                <div class="text-center mt-3">
                    <small class="text-muted">
                        <a href="/terms" class="text-decoration-none">Terms of Service</a>
                        <span class="mx-2">|</span>
                        <a href="/privacy" class="text-decoration-none">Privacy Policy</a>
                    </small>
                </div>
            </div>

            ${
                this.showDeleteConfirm
                    ? html`
                <div class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
                    <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content">
                            <div class="modal-body">
                                <p>Are you sure you want to delete your account? This cannot be undone.</p>
                            </div>
                            <div class="modal-footer">
                                <button class="btn btn-secondary" @click="${() => (this.showDeleteConfirm = false)}">Cancel</button>
                                <button class="btn btn-danger" @click="${this._confirmDeleteAccount}">Delete Account</button>
                            </div>
                        </div>
                    </div>
                </div>
            `
                    : ''
            }
        `;
    }

    _showPendingConfirm() {
        this.verificationEmail = this.pendingVerificationEmail;
        this.currentEmail = this.pendingCurrentEmail;
        this.awaitingCode = true;
        this.editing = true;
    }

    _renderViewMode() {
        const pc = this.pendingChanges;
        const pendingPicture = pc?.picture || null;
        const pendingUsername = pc?.username || null;
        const pendingEmail = pc?.email || null;

        return html`
            <div class="d-flex justify-content-end gap-1">
                <button class="btn btn-primary px-2 py-1" @click="${this._startEditing}" data-bs-toggle="tooltip" title="Edit profile">
                    <i class="bi bi-pencil-square" style="font-size: 1.1rem;"></i>
                </button>
                <button class="btn btn-danger px-2 py-1" @click="${this._handleDeleteAccount}" data-bs-toggle="tooltip" title="Delete account">
                    <i class="bi bi-trash" style="font-size: 1.1rem;"></i>
                </button>
            </div>
            <div class="text-center mb-4">
                <div style="position: relative; display: inline-block;">
                    ${
                        pendingPicture
                            ? html`<img src="${pendingPicture}" class="avatar mb-2" style="opacity: 0.6;" alt=""><span class="position-absolute bottom-0 start-50 translate-middle-x badge rounded-pill bg-secondary" style="font-size: 0.6rem;">Pending</span>`
                            : this.user.picture
                              ? html`<img src="${this.user.picture}" class="avatar mb-2" alt="">`
                              : html`<div class="avatar-placeholder mb-2"><i class="bi bi-person"></i></div>`
                    }
                </div>
                <h3>
                    ${pendingUsername || this.user.username}
                    ${pendingUsername ? html` <span class="badge bg-secondary" style="font-size: 0.5rem; vertical-align: middle;" data-bs-toggle="tooltip" title="Was: ${this.user.username}">Pending</span>` : ''}
                </h3>
                <p class="text-muted">
                    ${this.user.email}
                    ${pendingEmail ? html` <span><i class="bi bi-arrow-right"></i> ${pendingEmail} <span class="badge bg-secondary" style="font-size: 0.6rem;">Pending</span></span>` : ''}
                </p>
            </div>
            ${this._renderConnectedAccounts()}
            <button class="btn btn-outline-danger w-100" @click="${() => authState.logout()}"><i class="bi bi-box-arrow-right me-1"></i> Sign Out</button>
        `;
    }

    // Connected accounts: the providers this deployment offers, each either
    // linked (with a disconnect) or not (with a connect). A failed load still
    // renders the section so the error is visible — an absent section means
    // this deployment offers no providers, and a broken backend must not be
    // able to imitate that.
    _renderConnectedAccounts() {
        const view = connectedAccountsView(this.identityStatus, this.availableProviders);
        if (view === CONNECTED_ACCOUNTS_EMPTY) return '';

        return html`
            <hr class="my-4">
            <h6 class="text-muted mb-3">Connected accounts</h6>
            ${this.identityError ? html`<div class="alert alert-danger py-2"><small>${this.identityError}</small></div>` : ''}
            ${view === CONNECTED_ACCOUNTS_LIST ? this._renderProviderRows() : ''}
        `;
    }

    _renderProviderRows() {
        const linkedBy = new Map(this.identities.map((i) => [i.provider, i]));

        return html`
            <div class="list-group mb-4">
                ${this.availableProviders.map((provider) => {
                    const identity = linkedBy.get(provider);
                    return html`
                        <div class="list-group-item d-flex align-items-center justify-content-between">
                            <span>
                                <i class="bi ${providerIcon(provider)} me-2"></i>
                                ${providerLabel(provider)}
                                ${identity ? this._renderIdentityDetail(identity) : ''}
                                ${
                                    needsReconnect(identity)
                                        ? html`<span class="badge text-bg-warning ms-2">Needs reconnecting</span>`
                                        : ''
                                }
                            </span>
                            ${
                                identity
                                    ? html`<span class="d-flex gap-2">
                                          ${
                                              needsReconnect(identity)
                                                  ? html`<button class="btn btn-outline-primary btn-sm"
                                                        ?disabled="${this.identityBusy}"
                                                        @click="${() => this._handleLink(provider)}">Reconnect</button>`
                                                  : ''
                                          }
                                          <button class="btn btn-outline-secondary btn-sm"
                                              ?disabled="${this.identityBusy}"
                                              @click="${() => this._handleUnlink(provider)}">Disconnect</button>
                                      </span>`
                                    : html`<button class="btn btn-outline-primary btn-sm"
                                          ?disabled="${this.identityBusy}"
                                          @click="${() => this._handleLink(provider)}">Connect</button>`
                            }
                        </div>
                    `;
                })}
            </div>
        `;
    }

    _renderIdentityDetail(identity) {
        const detail = identityDisplayName(identity);
        return detail ? html`<small class="text-muted ms-1">${detail}</small>` : '';
    }

    async _loadIdentities() {
        try {
            const data = await api.get('/api/v1/identities');
            this.identities = data.identities || [];
            this.availableProviders = data.available || [];
            this.identityStatus = IDENTITY_STATUS_LOADED;
        } catch (err) {
            this.identityStatus = IDENTITY_STATUS_ERROR;
            this.identityError = err.message;
        }
    }

    async _handleLink(provider) {
        this.identityError = '';
        this.identityBusy = true;
        try {
            // Authenticated call first, then navigate: the round trip that
            // follows can't carry the bearer token. The server hands back a
            // ticket rather than the provider URL, because the CSRF nonce
            // cookie only sticks on a first-party navigation to the API.
            const data = await api.post('/api/v1/identities/link-start', {
                provider,
                return_to: '/profile',
            });
            window.location.assign(data.start_url);
        } catch (err) {
            this.identityError = err.message;
            this.identityBusy = false;
        }
    }

    async _handleUnlink(provider) {
        this.identityError = '';
        this.identityBusy = true;
        try {
            await api.delete(`/api/v1/identities/${encodeURIComponent(provider)}`);
            await this._loadIdentities();
        } catch (err) {
            this.identityError = err.message;
        } finally {
            this.identityBusy = false;
        }
    }

    _renderEditMode() {
        if (this.awaitingCode) return this._renderCodeConfirmation();

        const avatarSrc = this.editPicturePreview || this.user.picture;

        return html`
            <!-- Avatar -->
            <div class="text-center mb-4">
                <div class="avatar-edit" @click="${this._handleAvatarClick}">
                    ${
                        avatarSrc
                            ? html`<img src="${avatarSrc}" class="avatar" alt="">`
                            : html`<div class="avatar-placeholder"><i class="bi bi-person"></i></div>`
                    }
                    <i class="bi bi-camera-fill avatar-edit-icon"></i>
                    ${this.uploadingPicture ? html`<div class="avatar-uploading"><div class="spinner-border spinner-border-sm text-light" role="status"></div></div>` : ''}
                    ${this.editPicturePreview && !this.uploadingPicture ? html`<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-secondary" style="font-size: 0.65rem;">Pending</span>` : ''}
                </div>
                <input type="file" class="avatar-input" accept="image/*" @change="${this._handleAvatarChange}">
                <div class="mt-1" style="cursor: pointer;" @click="${this._handleAvatarClick}"><small class="text-muted">Click to change photo</small></div>
            </div>

            <!-- Username -->
            <div class="mb-3">
                <label for="username" class="form-label">Username</label>
                <input type="text" class="form-control" id="username" .value="${this.editUsername}" @input="${this._handleUsernameInput}">
                ${
                    this.editUsername !== this.user.username
                        ? this.usernameStatus === 'available'
                            ? html`<small class="text-success"><i class="bi bi-check-circle"></i> Available</small>`
                            : this.usernameStatus
                              ? html`<small class="text-danger"><i class="bi bi-x-circle"></i> ${this.usernameStatus}</small>`
                              : ''
                        : ''
                }
            </div>

            <!-- Email -->
            <div class="mb-4">
                <label for="email" class="form-label">Email</label>
                <input type="email" class="form-control" id="email" .value="${this.editEmail}" @input="${(e) => (this.editEmail = e.target.value)}">
            </div>

            <div class="d-flex gap-2">
                <button class="btn btn-primary flex-grow-1" ?disabled="${this.loading || this.uploadingPicture || !this._canSave()}" @click="${this._handleSave}">
                    ${this.uploadingPicture ? 'Uploading photo...' : this.loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button class="btn btn-outline-secondary" @click="${this._cancelEditing}">Cancel</button>
            </div>
        `;
    }

    _renderCodeConfirmation() {
        return html`
            <div class="text-center mb-3">
                <i class="bi bi-envelope-check" style="font-size: 3rem; color: #0d6efd;"></i>
            </div>
            <p class="text-center mb-3">A confirmation code was sent to <strong>${this.verificationEmail}</strong>.</p>
            ${this.error ? html`<div class="alert alert-danger py-2 small">${this.error}</div>` : ''}
            <div class="d-flex justify-content-center mb-3">
                <input type="text" class="form-control code-input" maxlength="6" inputmode="numeric" pattern="[0-9]{6}" autocomplete="one-time-code" placeholder=""
                    aria-label="Code sent to ${this.verificationEmail}"
                    .value="${this.code}" @input="${(e) => (this.code = e.target.value.replace(/\D/g, ''))}">
            </div>
            ${
                this.currentEmail
                    ? html`
                <p class="text-center mb-3">To approve moving your account, enter the code we sent to your current address, <strong>${this.currentEmail}</strong>.</p>
                <div class="d-flex justify-content-center mb-3">
                    <input type="text" class="form-control code-input" maxlength="6" inputmode="numeric" pattern="[0-9]{6}" placeholder=""
                        aria-label="Code sent to ${this.currentEmail}"
                        .value="${this.currentEmailCode}" @input="${(e) => (this.currentEmailCode = e.target.value.replace(/\D/g, ''))}">
                </div>
            `
                    : ''
            }
            <div class="d-flex gap-2">
                <button class="btn btn-primary flex-grow-1" ?disabled="${this.loading || !hasEveryCode(this.code, this.currentEmailCode, this.currentEmail)}" @click="${this._handleConfirm}">
                    ${this.loading ? 'Confirming...' : 'Confirm Changes'}
                </button>
                <button class="btn btn-outline-secondary" @click="${this._cancelEditing}">Cancel</button>
            </div>
        `;
    }
}

customElements.define('profile-view', ProfileView);
export { ProfileView };
